/* eslint-disable indent */
import { AfterViewInit, Component, HostListener, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs/internal/Observable';
import { take, withLatestFrom } from 'rxjs/operators';
import { OrdersService } from 'src/app/inventory/components/receive-edi/orders.service';
import { getDateFromISO } from '../../helpers/helpers';
import { ActivityTypesEnum } from '../../interfaces/ActivityTypesEnum';
import { AlertTypesEnum } from '../../interfaces/AlertTypesEnum';
import { BadgeColorsEnum } from '../../interfaces/BadgeColorsEnum';
import { TransactionDateError } from '../../interfaces/IValidatedTransactionDate';
import { OrderTypeEnum } from '../../interfaces/OrderTypeEnum';
import { IAlertMessage } from '../../interfaces/alert-message.interface';
import { IAmendTransaction } from '../../interfaces/amend-transaction.interface';
import { IntegrationTypeEnum } from '../../interfaces/integration.interface';
import { TransactionSearchCriteria } from '../../interfaces/search-criteria';
import { ITransaction } from '../../interfaces/transaction.interface';
import { IUpdateTransactionDateResponse } from '../../interfaces/update-transaction-date-request.interface';
import { IUser } from '../../interfaces/user.interface';
import { SideWidgetService } from '../../services/side-widget.service';
import { TransactionsService } from '../../services/transactions.service';
import { AuthState } from '../../states/auth/auth.state';
import { SetCurrentInventoryFromDrugID } from '../../states/inventory/inventory.actions';
import { PharmacyState } from '../../states/pharmacy/pharmacy.state';
import {
  AmendCompound,
  AmendTransaction,
  GetMostRecentAuditTransactionDate,
  GetTransaction,
  GetTransactionDateHistory,
  GetWidgetHistory,
  SetWidgetData,
  UpdateTransactionDate,
} from '../../states/transactions/transactions.actions';
import { TransactionsState } from '../../states/transactions/transactions.state';
import { AlertMessageService } from '../../services/alert-message.service';
import { ICompoundAmendRequestPayload } from '../../interfaces/compound-amend-request.interface';
import { BackCountService } from '../../services/back-count.service';

@UntilDestroy()
@Component({
  selector: 'app-record-details-widget',
  templateUrl: './record-details-widget.component.html',
  styleUrls: ['./record-details-widget.component.scss'],
})
export class RecordDetailsWidgetComponent implements OnInit, AfterViewInit {
  userId: string | null | undefined;

  show: boolean;
  showAmendingDialog: boolean;

  widgetData: ITransaction;
  mostRecentData: ITransaction;
  public pendingTransactions: ITransaction[] = [];

  displayTransactionDateHistory: boolean;

  BadgeColorsEnum = BadgeColorsEnum;
  ActivityTypesEnum = ActivityTypesEnum;

  getDateFromISO = getDateFromISO;
  AlertTypesEnum = AlertTypesEnum;

  focusPin: boolean;
  showPinDialog: boolean;
  showWitnessPinDialog: boolean;

  showUpdateTransactionPinDialog: boolean;

  alertMessage: IAlertMessage;
  autohideDelay = 3;

  showResetPinDialog: boolean;

  currentPinDialog: 'sign' | 'witness';

  amendment: IAmendTransaction = {} as any;
  compoundAmendment: ICompoundAmendRequestPayload = {} as any;

  mostRecentAuditDate: Date;

  mostRecentActivityDate: string | Date;
  mostRecentActivityUserFirstName: string;
  mostRecentActivityUserLastName: string;
  amendableIfCompound: boolean;

  orderDetails: {header1: string; value1: string, header2: string, value2: string};

  OrderTypeEnum = OrderTypeEnum;

  amendForbiddenOperationActivities = [ActivityTypesEnum.Amendment, ActivityTypesEnum.Audit, ActivityTypesEnum.DamagedExpired, ActivityTypesEnum.MovedFrom, ActivityTypesEnum.MovedTo];

  @Select(TransactionsState.success) success$: Observable<boolean>;
  @Select(TransactionsState.widgetData) widgetData$: Observable<ITransaction>;
  @Select(TransactionsState.widgetHistory) widgetHistory$: Observable<ITransaction[]>;
  @Select(TransactionsState.widgetHistoryHasMore) widgetHistoryHasMore$: Observable<boolean>;
  @Select(AuthState.activeUser) user$: Observable<IUser>;
  @Select(TransactionsState.transactionDateChangeHistory) transactionDateChangeHistory$: Observable<any>;
  @Select(TransactionsState.mostRecentAuditTransactionDate) mostRecentAuditTransactionDate$: Observable<Date>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;

  inventoryLocationsEnabled: boolean;
  
  @ViewChild('fixedWrapper') fixedWrapper;
  @ViewChild('scrollWrapper') scrollWrapper;
  @ViewChildren('amendHistoryItems') amendHistoryItems;
  @ViewChild('pinUser') pinUserDialog;
  @ViewChild('pinWitness') pinWitnessDialog;

  @ViewChild('editTransactionDateComponent') editTransactionDateComponent;

  @HostListener('document:keydown.escape', ['$event'])
  keydownHandler(event: KeyboardEvent): void {
    this.close();
  }

  transactionDate: string;
  editTransactionDate: boolean;

  selectedDate: Date;
  cardLogoClass: string;

  constructor(
    private router: Router,
    private store: Store,
    private transactionsService: TransactionsService,
    private orderService: OrdersService,
    private widgetService: SideWidgetService,
    private alertService: AlertMessageService,
    private backCountService: BackCountService
  ) {
  }

  ngOnInit(): void {
    this.userId = this.store.selectSnapshot(AuthState.activeUser)?.id;
    this.widgetData$
      .pipe(untilDestroyed(this))
      .subscribe((record: ITransaction) => {
        this.widgetData = record;
        setTimeout(() => {
          this.show = !!record;
          if (this.show) {
            if (this.fixedWrapper && this.fixedWrapper.nativeElement) {
              this.scrollWrapper?.nativeElement.style
              .setProperty('height', 'calc(100vh - ' + (this.fixedWrapper.nativeElement.clientHeight + 1) + 'px)');
            } 
            if (record && record.drug.id) {
              const searchCriteria = new TransactionSearchCriteria();
              searchCriteria.drugId = record.drug.id;
              searchCriteria.pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);
            }

            this.store.dispatch(new GetWidgetHistory(record.drug.id))
            .pipe(untilDestroyed(this))
            .subscribe(() => {
              this.updateMostRecentActivity();
              this.initAmendableIfCompound();
            });

            this.store.dispatch(new GetMostRecentAuditTransactionDate(record.drug.id, record.locationID));
          }
        });
        this.mostRecentData = record?.history && record?.history[0];
        this.pendingTransactions = record?.history.filter(x=> !!x.pendingTransaction).map(x => x.pendingTransaction);

        this.orderDetails = this.getOrderDetails(this.mostRecentData);
        //init most recent activity based on the transaction
        this.mostRecentActivityDate = this.mostRecentData?.createDate;
        this.mostRecentActivityUserFirstName = this.mostRecentData?.firstName;
        this.mostRecentActivityUserLastName = this.mostRecentData?.lastName;

        this.inventoryLocationsEnabled$
        .pipe(untilDestroyed(this))
        .subscribe(val => this.inventoryLocationsEnabled = val);
      });
      
    //refresh history
    this.transactionsService.transactionDateSuccessfullyUpdated$
    .pipe(untilDestroyed(this))
    .subscribe(() => {
      this.store.dispatch(new GetWidgetHistory(this.widgetData.drug.id))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.updateMostRecentActivity();
        this.refreshIncorrectQtyAmendTransactionDate();
        this.initAmendableIfCompound();
      });
    });

    this.mostRecentAuditTransactionDate$
    .pipe(untilDestroyed(this))
    .subscribe((date) => this.mostRecentAuditDate = new Date(date));
  }

  ngAfterViewInit(): void {
    this.scrollWrapper?.nativeElement.style.setProperty('height', '0px');
  }

  close(): void {
    this.show = false;
    setTimeout(() => {
      this.store.dispatch(new SetWidgetData(null));
    }, 250); //time for animation to happen before html of the component gets removed from DOM
    
  }

  openRecentActivity(drugId: string): void {
    this.close();

    if (this.router.url !== '/inventory/' + drugId) {
      this.router.navigate(['inventory', drugId]);
    } else {
      this.transactionsService.widgetTransaction$.next(drugId);
    }

    this.store.dispatch(new SetCurrentInventoryFromDrugID(drugId));
  }

  updateTransactions(): void {
    this.store.dispatch(new GetTransaction(this.widgetData.id))
    .subscribe(_ => {
      this.transactionsService.transactionSuccessfullyAmended$.next();
    });
   
  }

  openAmendDialog(): void {
    this.showAmendingDialog = true;
  }

  cancelAmend(): void {
    this.showAmendingDialog = false;
    this.amendment = null;
  }

  openPinDialog(amendment): void {
    if(this.widgetData.activityType === ActivityTypesEnum.Compounded) {
      this.compoundAmendment = amendment;
    } else {
      this.amendment = amendment;
      this.amendment.createById = this.userId;
    }
    this.showPinDialog = true;
    this.focusPin = true;
    this.currentPinDialog = 'sign';
  }

  closePinDialog(): void {
    this.showPinDialog = false;
    this.showUpdateTransactionPinDialog = false;
    this.focusPin = false;
  }
  
  afterUpdateTransactionDatePinHandled({success, pin}): void {
    if (success) {
      this.closePinDialog();
      this.store.dispatch(new UpdateTransactionDate(
        {
          transactionId: this.mostRecentData?.id, 
          transactionDate: this.selectedDate,
          gpi: this.mostRecentData.drug.gpi,
          pin
        })).pipe(
        untilDestroyed(this)
      )
    .subscribe(() => {
      const response = this.store.selectSnapshot(TransactionsState.response);
      const value = response?.value as IUpdateTransactionDateResponse;
      if (response?.success && !value?.isNegativeBalanceError && response.value?.transactionDateError === TransactionDateError.none) {
        this.transactionsService.transactionDateSuccessfullyUpdated$.next();
        this.editTransactionDateComponent?.close(this.selectedDate);
        this.mostRecentData = {...this.mostRecentData, isTransactionDateUpdated: true};
      } else {
        this.transactionsService.handleTransactionDateErrors(response?.value);
      }
    })
    }
  }

  afterPinHandled({success, pin}): void {
    if (success) {
      this.closePinDialog();
      this.showWitnessPinDialog = true;
      this.currentPinDialog = 'witness';
      this.amendment.userPin = parseInt(pin, 10);
      this.compoundAmendment.userPin =  parseInt(pin, 10);
    }
  }

  closeWitnessPinDialog(): void {
    this.showWitnessPinDialog = false;
  }

  afterWitnessPinHandled({success, pin}): void {
    if (success) {
      this.amendment.witnessPin = parseInt(this.pinWitnessDialog.pinValue, 10);
      this.compoundAmendment.witnessPin = parseInt(this.pinWitnessDialog.pinValue, 10);
      this.closeWitnessPinDialog();
      this.amendTransaction();
    }
  }

  amendTransaction(): void {
    this.showAmendingDialog = false;
    let request: Observable<any>;
    //compound amend
    if (this.widgetData.activityType === ActivityTypesEnum.Compounded) {
      request = this.store.dispatch(new AmendCompound(this.compoundAmendment));
    } else { //regular amend
      request = this.store.dispatch(new AmendTransaction(this.amendment, this.widgetData.id, this.widgetData.drug.friendlyName, this.widgetData.drug.packageNdc));
    }
      request
      .pipe(take(1), withLatestFrom(this.success$))
      .subscribe(([_, success]) => {
        if (success) {
          this.alertService.displayAlert('Successfully amended.', AlertTypesEnum.Success);
        } else {
          this.alertService.displayAlert('Amendment failed.', AlertTypesEnum.Error);
        }

        if (success) {
          this.updateTransactions();
          if (this.widgetData.activityType === ActivityTypesEnum.Compounded) {
            this.backCountService.handleBackCountPillUpdate([{isBackCountMatches: this.compoundAmendment.isCompoundBackCountMatches}, ...this.compoundAmendment.ingredientsWaste]);
          } else {
            this.backCountService.handleBackCountPillUpdate(this.amendment);
          }
        }
      });
  }

  openResetPinDialog(): void {
    this.showPinDialog = false;
    this.focusPin = false;
    this.showWitnessPinDialog = false;
    this.showResetPinDialog = true;
  }

  closeResetPinDialog(): void {
    this.showResetPinDialog = false;

    if (this.currentPinDialog === 'sign') {
      this.showPinDialog = true;
      this.focusPin = true;
    } else {
      this.showWitnessPinDialog = true;
    }
  }

  afterPinUpdated(success: boolean): void {
    // TODO: show message about pin update
    if (success) {
      this.closeResetPinDialog();
    }
  }

  updateTransactionDate(date: Date) {
    this.selectedDate = date;
    this.showUpdateTransactionPinDialog = true;
  }

  onHistoryClick(): void {
    if (this.store.selectSnapshot(TransactionsState)?.transactionDateChangeHistory?.transactionId !== this.mostRecentData?.id) {
      this.getTransactionDateHistory()
      .pipe(untilDestroyed(this))
      .subscribe(() => this.displayTransactionDateHistory = true);
    } else {
      this.displayTransactionDateHistory = true;
    }
  }

  private getTransactionDateHistory(): Observable<any> {
    return this.store.dispatch(new GetTransactionDateHistory(this.mostRecentData?.id));
  }
  
  canUpdateTransactionDate(): boolean {
    if (this.mostRecentAuditDate) {
      return this.mostRecentData.activityType !== ActivityTypesEnum.Audit &&
      this.mostRecentAuditDate < new Date(this.mostRecentData?.transactionDate);
    } 
    return false;
  }

  openOrder(transaction: ITransaction): void {
    setTimeout(() => {
      this.widgetService.closeSideWidget();
    });
    this.orderService.navigateToOrderDetails(this.router, transaction?.orderType, transaction.orderId, transaction.drug.friendlyName);
  }

  openAudit(id: number): void {
    this.router.navigate(['audits/details', id], {state : {navigatedFrom: this.router.url, drugName: this.widgetData?.drug?.friendlyName}});
  }

  openCompound(id: number): void {
    this.router.navigate(['inventory/compounds', id], {state : {navigatedFrom: this.router.url, drugName: this.widgetData?.drug?.friendlyName}});
  }

  getLogoUrl(integrationType: IntegrationTypeEnum): string {
    switch (integrationType) {
      case IntegrationTypeEnum.LibertyRx: {
        this.cardLogoClass = 'libertyRx';
        return '/assets/images/liberty-logo.png'
      }
      case IntegrationTypeEnum.PioneerRx: {
        this.cardLogoClass = 'pioneerRx';
        return '/assets/images/pioneerRx-logo.png'
      }
      case IntegrationTypeEnum.FrameworkLTC: {
        this.cardLogoClass = 'frameworkLTC';
        return '/assets/images/frameworkLTC-logo.png'
      }
    
      default:
        break;
    }
  }

  public getOrderDetails(transaction: ITransaction): {header1: string; value1: string, header2: string, value2: string} {
    switch (transaction?.orderType) {
      case OrderTypeEnum.Purchase:
        return {
          header1: 'Wholesaler',
          value1: transaction?.orderWholesalerName,
          header2: 'Invoice #',
          value2: transaction?.orderInvoiceNumber
        }
        case OrderTypeEnum.SaleToProvider:
          return {
            header1: 'Provider',
            value1: transaction?.orderProviderName,
            header2: 'PO #',
            value2: transaction?.orderPurchaseOrder
        }
        case OrderTypeEnum.SaleToReverseDistributor:
          return {
            header1: 'Reverse Distributor',
            value1: transaction?.orderReverseDistributorName,
            header2: 'PO #',
            value2: transaction?.orderPurchaseOrder
        }
        case OrderTypeEnum.ReturnToWholesaler:
          return {
            header1: 'Wholesaler',
            value1: transaction?.orderWholesalerName,
            header2: 'MRA #',
            value2: transaction?.orderMRA
        }
        case OrderTypeEnum.Destruction:
          return {
            header1: 'Name of  of Waste / Destruction',
            value1: transaction?.orderNameOfDestruction,
            header2: 'Place of of Waste / Destruction',
            value2: transaction?.orderPlaceOfDestructionName
        }
    
      default:
        return null;
    }
  }


  private refreshIncorrectQtyAmendTransactionDate(): void {
    let history = [...this.widgetData.history];
    if(history[1]?.reason === 'Recorded incorrect quantity') {
      history[1] = {
        ...history[1],
        transactionDate: this.selectedDate 
      };
    }
    this.widgetData = {...this.widgetData, history: history};
    this.selectedDate = null;
  }


  /**
   * @description
   * Update most recent activity based on the last transaction date update
   * 
 */
  private updateMostRecentActivity(): void {
    const mostRecentTransactionDateChangeHistoryRecord = this.store.selectSnapshot(TransactionsState)?.widgetHistory[0]?.mostRecentTransactionDateChangeHistoryRecord;
    if(mostRecentTransactionDateChangeHistoryRecord) {
      this.mostRecentActivityDate = mostRecentTransactionDateChangeHistoryRecord?.createDate;
      this.mostRecentActivityUserFirstName = mostRecentTransactionDateChangeHistoryRecord?.userFirstName;
      this.mostRecentActivityUserLastName = mostRecentTransactionDateChangeHistoryRecord?.userLastName;
    }
  }

  private initAmendableIfCompound(): void {
    //don't combine the filter expression with the some expression since it will always return false if filtered is empty
    const filteredTransactionGoingAfterTheCurrentOne = (this.store.selectSnapshot(TransactionsState)?.widgetHistory as ITransaction[])
    ?.filter(x => x.transactionDate > this.mostRecentData?.transactionDate);

    if(filteredTransactionGoingAfterTheCurrentOne.length) {
      //If something else recorded after the compound besides another compound
      this.amendableIfCompound = filteredTransactionGoingAfterTheCurrentOne?.every(x => x.activityType === ActivityTypesEnum.Compounded);
    } else {
      this.amendableIfCompound = true;
    }
  }
  
}
